import { SlidersHorizontal, User } from 'lucide-react';
import { useCallback, useId, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Badge } from 'v5/design-system/Badge';
import { Button } from 'v5/design-system/Button';
import { SimpleTooltip } from 'v5/design-system/Tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from 'v5/design-system/Popover';
import { FieldChooser } from 'v5/patterns/fields-chooser/FieldChooser';
import { FieldChooserColumn } from 'v5/patterns/fields-chooser/FieldChooserColumn';
import { SectionedChecklist } from 'v5/patterns/widgets/SectionedChecklist';
import { usePortalContainer } from 'v5/primitives/PortalContainer';
import { DEFAULT_RESOURCE_PICKER_RESULT_FIELDS } from 'v5/services/front/UserPreferencesService/UserPreferencesService';
import {
  MAX_LINE_FIELDS,
  resultFieldsUtils,
  RIGHT_FIELDS
} from './resultFields.logic';
import { useResourceFieldSections } from './useResourceFieldSections';
import type { ResultFields } from './useResultFields';

/**
 * What each result of a picker shows, chosen by the user for every picker: the fields of the line
 * under the name, in order, and what shows on the right (list fields as badges, the pictures). A popover beside
 * List / Gallery.
 */
export function ResultFieldsButton({ fields }: { fields: ResultFields }) {
  const { t } = useTranslation('resource-fields', {
    keyPrefix: 'ResultFields'
  });
  const { value, onChange, labelOf } = fields;
  const catalog = useResourceFieldSections(fields.catalog);
  const [open, setOpen] = useState(false);
  const rightTitleId = useId();
  const portalContainer = usePortalContainer();
  // in a dialog the popover stays inside it, clear of its edge, narrower if it has to be
  const [boundary, setBoundary] = useState<Element | null>(null);
  const triggerRef = useCallback(
    (trigger: HTMLButtonElement | null) =>
      setBoundary(trigger?.closest('[role="dialog"]') ?? null),
    []
  );
  return (
    <Popover modal open={open} onOpenChange={setOpen}>
      {open &&
        // modal turns pointer events off on the page, but a virtualized list turns them back on
        // for its rows: this layer takes a press outside, which only closes the popover
        createPortal(
          <div
            aria-hidden
            className="pointer-events-auto fixed inset-0 z-50"
            onPointerDown={() => setOpen(false)}
          />,
          portalContainer
        )}
      <SimpleTooltip title={t('button')}>
        <PopoverTrigger asChild ref={triggerRef}>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            layout="icon"
            aria-label={t('button')}
          >
            <SlidersHorizontal />
          </Button>
        </PopoverTrigger>
      </SimpleTooltip>
      <PopoverContent
        align="end"
        collisionBoundary={boundary}
        collisionPadding={12}
        aria-label={t('title')}
        // three columns where it has the room; in a narrow place one under another, and it scrolls
        className="@container/fields flex max-h-(--radix-popover-content-available-height) w-[1140px] max-w-[min(1140px,var(--radix-popover-content-available-width))] flex-col overflow-y-auto p-0"
      >
        <div className="flex items-center gap-2 border-b border-border-default px-4 py-2">
          <h3 className="text-base font-semibold">{t('title')}</h3>
          <Badge variant="gray" tone="subtle" size="sm">
            <User />
            {t('onlyMe')}
          </Badge>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="ml-auto"
            onClick={() => onChange(DEFAULT_RESOURCE_PICKER_RESULT_FIELDS)}
          >
            {t('reset')}
          </Button>
        </div>
        {/* side by side the columns scroll on their own: the popover gives them one height */}
        <div
          data-testid="result-fields-columns"
          className="grid grid-cols-1 divide-y divide-border-default [&>:first-child]:h-[470px] @min-[880px]/fields:h-[28rem] @min-[880px]/fields:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)_minmax(0,0.85fr)] @min-[880px]/fields:divide-x @min-[880px]/fields:divide-y-0 @min-[880px]/fields:[&>:first-child]:h-auto"
        >
          <FieldChooser
            catalog={catalog.sections}
            catalogLabel={catalog.label}
            emptyCatalog={t('noCatalog')}
            picked={value.line}
            labelOf={labelOf}
            onPick={({ added, removed }) =>
              onChange(
                added.reduce(
                  resultFieldsUtils.add,
                  removed.reduce(resultFieldsUtils.remove, value)
                )
              )
            }
            onReorder={line => onChange(resultFieldsUtils.reorder(value, line))}
            limit={MAX_LINE_FIELDS}
            pickedTitle={t('line')}
            pickedMeta={t('lineLimit', { max: MAX_LINE_FIELDS })}
            orderLabel={t('order')}
            emptyPicked={t('lineEmpty')}
          />
          <FieldChooserColumn
            aria-labelledby={rightTitleId}
            header={
              <div className="flex h-9 items-center">
                <h3 id={rightTitleId} className="text-base font-semibold">
                  {t('right')}
                </h3>
              </div>
            }
          >
            <p className="text-sm text-fg-muted">{t('rightHint')}</p>
            <SectionedChecklist
              aria-label={t('right')}
              className="-mx-4"
              sections={[
                {
                  id: 'right',
                  items: RIGHT_FIELDS.map(key => ({
                    id: key,
                    label: key === 'pictures' ? t('pictures') : labelOf(key)
                  }))
                }
              ]}
              selected={new Set(value.right)}
              onChange={shown =>
                onChange(resultFieldsUtils.setRight(value, shown))
              }
              emptyState={null}
            />
          </FieldChooserColumn>
        </div>
      </PopoverContent>
    </Popover>
  );
}
